import cx from 'classnames';
import {isEqual, min} from 'lodash';
import {PureComponent, createRef} from 'react';
import Draggable from 'react-draggable';
import {withTranslation, WithTranslation} from 'react-i18next';

import {GroupRoomManager, RoomEvent} from '../shared/ConferenceRoom';
import {ResizableBoxWrapper} from 'components';
import {LOCAL_MEDIA_ID, REMOTE_MEDIA_ID, VIDEO_PIN_AUTO_HIDE_TIMEOUT} from 'constants/session';
import {themeConfig} from 'constants/theme';
import {VideoPinParticipant} from 'enums';
import {MediaDevicesConstraints} from 'types';
import {mediaDevicesUtils} from 'utils';

import {PeerConnectionId} from '../shared';

import './styles.scss';

const miniConstrains = {
    // [width, height]
    minConstraints: [220, 165] as [number, number],
    maxConstraints: [480, 360] as [number, number]
};

type State = {
    readonly parentHeight: number;
    readonly remoteVideoHeight: number;
    readonly parentWidth: number;
    readonly remoteVideoWidth: number;
    readonly localVideoOverlayVisible: boolean;
    readonly localVideoOverlayTimeout: ReturnType<typeof setTimeout>;
    readonly remoteVideoOverlayVisible: boolean;
    readonly remoteVideoOverlayTimeout: ReturnType<typeof setTimeout>;
    readonly trackSubscribeChangeDate: Date; // updated after new track published event to trigger re-render
}

const initialState: State = {
    parentHeight: null,
    remoteVideoHeight: null,
    parentWidth: null,
    remoteVideoWidth: null,
    localVideoOverlayVisible: false,
    localVideoOverlayTimeout: null,
    remoteVideoOverlayVisible: false,
    remoteVideoOverlayTimeout: null,
    trackSubscribeChangeDate: null
};

type Props = {
    participantId?: string;
    room?: GroupRoomManager;
    pinParticipant?: string;
    mediaConstraints?: MediaDevicesConstraints;
    isFullScreen?: boolean;
    skipAutoDetectFullScreen?: boolean;
    isAudioEnabled?: boolean;
    isVideoEnabled?: boolean;
    mini?: boolean;
    miniPositionClassName?: string;
    overLocalVideoContent?: React.ReactNode,
    overRemoteVideoContent?: React.ReactNode,
    parentRect?: {
        width: number,
        height: number
    };
    toggleFullScreen?: (value: boolean) => void;
    onConnectError?: () => void;
} & WithTranslation;

class KurentoVideo extends PureComponent<Props, State> {
    readonly state = initialState;
    static readonly defaultProps = {
        isAudioEnabled: true,
        isVideoEnabled: true,
        miniPositionClassName: 'tw-absolute tw-top-4 tw-left-4'
    };

    _isMounted = false;
    subscriptions = [];
    localMediaRef = createRef<HTMLDivElement>();
    remoteMediaRef = createRef<HTMLDivElement>();
    otherRemoteMediaRef = createRef<HTMLDivElement>();
    componentRef = createRef<HTMLDivElement>();

    componentDidMount() {
        this._isMounted = true;
        window.addEventListener('resize', this.updateDimensions);

        const {skipAutoDetectFullScreen} = this.props;

        if (this.props.room.isRoomStateConnected) {
            this.startPeerConnection();
        }

        this.subscriptions.push(
            this.props.room.on(RoomEvent.Connected, this.startPeerConnection),
            this.props.room.on(RoomEvent.PeerConnectionReady, ({peerId}: {peerId: string}) => {
                if (peerId === PeerConnectionId.AudioVideo) {
                    this.toggleAudio();
                    this.toggleVideo();
                }
            })
        );

        if (this.props.onConnectError) {
            this.subscriptions.push(this.props.room.on(RoomEvent.ConnectError, this.props.onConnectError));
        }

        setTimeout(() => {
            this.updateDimensions();
            if (!skipAutoDetectFullScreen) {
                this.setDefaultFullScreen();
            }
        });
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.mini !== prevProps.mini) {
            this.updateDimensions();
        }
        if (this.props.isAudioEnabled !== prevProps.isAudioEnabled) {
            this.toggleAudio();
        }
        if (this.props.isVideoEnabled !== prevProps.isVideoEnabled) {
            this.toggleVideo();
        }
        if (this.props.isFullScreen !== prevProps.isFullScreen) {
            this.updateDimensions();
        }
        if (this.props.mediaConstraints &&
            !mediaDevicesUtils.isUsedInputDevicesToggled(this.props.mediaConstraints, prevProps.mediaConstraints)) {
            if (!isEqual(this.props.mediaConstraints?.audioinput, prevProps.mediaConstraints?.audioinput) ||
            !isEqual(this.props.mediaConstraints?.videoinput, prevProps.mediaConstraints?.videoinput)) {
                this.updateInputDevices(this.props.mediaConstraints);
            }
            if (!isEqual(this.props.mediaConstraints?.audiooutput, prevProps.mediaConstraints?.audiooutput)) {
                this.updateAudioOutputDevice(this.props.mediaConstraints?.audiooutput);
            }
        }
        if (this.props.parentRect?.width !== prevProps.parentRect?.width ||
            this.props.parentRect?.height !== prevProps.parentRect?.height) {
            setTimeout(() => {
                this.updateDimensions();
            });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.props.room.stopPeerConnection(PeerConnectionId.AudioVideo);
        window.removeEventListener('resize', this.updateDimensions);
        this.subscriptions.forEach((unsubscribe) => unsubscribe());
    }

    startPeerConnection = () => {
        this.props.room.startPeerConnection(PeerConnectionId.AudioVideo, {
            constraints: {
                audio: this.props.mediaConstraints?.audioinput,
                video: this.props.mediaConstraints?.videoinput
            },
            localParticipantElement: this.localMediaRef.current,
            mainRemoteParticipantElement: this.remoteMediaRef.current,
            otherRemoteParticipantsElement: this.otherRemoteMediaRef.current,
            audiooutput: this.props.mediaConstraints?.audiooutput
        });
    };

    setDefaultFullScreen = (): void => {
        const rect = this.getClientRect();
        if (rect) {
            const {width: rectWidth, height: rectHeight} = rect;

            const initialFullScreen = rectWidth > rectHeight ? false : true;
            this.props.toggleFullScreen?.(initialFullScreen);
        }
    };

    private getClientRect = () => {
        const refToSize = this.componentRef;

        if (!refToSize || !refToSize.current) {
            return null;
        }

        const parent: HTMLElement | null = (refToSize.current as HTMLElement).parentElement;
        if (!parent) {
            return null;
        }

        return parent.getBoundingClientRect();
    };

    updateDimensions = () => {
        const refToSize = this.componentRef;
        if (!refToSize || !refToSize.current) {
            return;
        }

        const rect = this.getClientRect();
        if (!rect) {
            return;
        }

        const {width: rectWidth, height: rectHeight} = rect;
        const {isFullScreen, parentRect, pinParticipant} = this.props;

        let width: number;
        let height: number;

        if (rectWidth > rectHeight) {
            width = rectWidth;
            height = width * 3 / 4;
            if (!isFullScreen && height > rectHeight) {
                height = rectHeight;
                width = height / 3 * 4;
            }

            refToSize.current.style.width = isFullScreen ? '100%' : width + 'px';
            (refToSize.current as HTMLElement).style.height = (height < rectHeight ? height : rectHeight) + 'px';
        } else {
            height = rectHeight;
            width = height / 3 * 4;
            if (!isFullScreen && width > rectWidth) {
                width = rectWidth;
                height = width * 3 / 4;
                // workaround for column positioned base medias
                if (!pinParticipant && rectWidth < themeConfig.screens.md) {
                    height = 2 * height;
                }
            }

            refToSize.current.style.height = isFullScreen ? '100%' : height + 'px';
            (refToSize.current as HTMLElement).style.width = width + 'px';
        }

        const minWidth = miniConstrains.minConstraints[0];
        const minHeight = miniConstrains.minConstraints[1];
        if (this.props.mini && (width > minWidth || height > minHeight)) {
            height = minHeight;
            width = minWidth;
        }

        const parentHeight = parentRect ? parentRect.height : window.innerHeight;
        const parentWidth = parentRect ? parentRect.width : window.innerWidth;

        const remoteVideoHeight = height;
        const remoteVideoWidth = width;

        this.setState({parentHeight, remoteVideoHeight, parentWidth, remoteVideoWidth});
    };

    updateInputDevices = (constraints: MediaDevicesConstraints) => {
        if (mediaDevicesUtils.hasSupportRestartDevices()) {
            this.props.room.localParticipant?.restartInputDevices(PeerConnectionId.AudioVideo, constraints);
        } else {
            this.props.room.stopLocalPeerConnection(PeerConnectionId.AudioVideo);
            this.startPeerConnection();
        }
    };

    updateAudioOutputDevice = (deviceId: string) => {
        this.props.room.localParticipant?.restartOutputDevice(PeerConnectionId.AudioVideo, deviceId);
    };

    toggleAudio = () => {
        this.props.room.localParticipant?.toggleAudioTracks(PeerConnectionId.AudioVideo, this.props.isAudioEnabled);
    };

    toggleVideo = () => {
        this.props.room.localParticipant?.toggleVideoTracks(PeerConnectionId.AudioVideo, this.props.isVideoEnabled);
    };

    toggleFullScreen = () => {
        setTimeout(this.updateDimensions);
    };

    handleMouseMoveLocalVideo = () => {
        if (this.state.localVideoOverlayTimeout) {
            clearTimeout(this.state.localVideoOverlayTimeout);
        }

        const timeout = setTimeout(() => {
            if (this._isMounted) {
                this.setState({localVideoOverlayVisible: false});
            }
        }, VIDEO_PIN_AUTO_HIDE_TIMEOUT);
        this.setState({localVideoOverlayTimeout: timeout, localVideoOverlayVisible: true});
    };

    handleMouseOutLocalVideo = () => {
        if (this.state.localVideoOverlayTimeout) {
            clearTimeout(this.state.localVideoOverlayTimeout);
        }
        this.setState({localVideoOverlayVisible: false, localVideoOverlayTimeout: null});
    };

    handleMouseMoveRemoteVideo = () => {
        if (this.state.remoteVideoOverlayTimeout) {
            clearTimeout(this.state.remoteVideoOverlayTimeout);
        }
        const timeout = setTimeout(() => {
            if (this._isMounted) {
                this.setState({remoteVideoOverlayVisible: false});
            }
        }, VIDEO_PIN_AUTO_HIDE_TIMEOUT);
        this.setState({remoteVideoOverlayTimeout: timeout, remoteVideoOverlayVisible: true});
    };

    handleMouseOutRemoteVideo = () => {
        if (this.state.remoteVideoOverlayTimeout) {
            clearTimeout(this.state.remoteVideoOverlayTimeout);
        }
        this.setState({remoteVideoOverlayVisible: false, remoteVideoOverlayTimeout: null});
    };

    render() {
        const {
            componentRef, localMediaRef, remoteMediaRef, otherRemoteMediaRef, handleMouseMoveLocalVideo, handleMouseMoveRemoteVideo,
            handleMouseOutLocalVideo, handleMouseOutRemoteVideo,
            state: {remoteVideoHeight, parentHeight, remoteVideoWidth, parentWidth, localVideoOverlayVisible, remoteVideoOverlayVisible},
            props: {
                mini, miniPositionClassName, isFullScreen, overLocalVideoContent, overRemoteVideoContent,
                pinParticipant
            }
        } = this;

        const isLandscape = parentHeight < parentWidth;
        const remoteVideoDisabled = false; // TODO: calculate

        return (
            <div
                ref={componentRef}
                className={
                    cx('kurento-video-container tw-bg-default',
                        {
                            'mini tw-max-w-0 tw-max-h-0 tw-overflow-visible tw-z-1': mini,
                            [miniPositionClassName]: miniPositionClassName && mini
                        })
                }
            >
                <Draggable handle='.handle' disabled={!mini} position={mini ? null : {x: 0, y: 0}}>
                    <div>
                        <ResizableBoxWrapper
                            disabled={!mini}
                            width={min([remoteVideoWidth, parentWidth]) || 0}
                            height={min([remoteVideoHeight, parentHeight]) || 0}
                            lockAspectRatio
                            {...(mini && miniConstrains)}
                        >
                            <div className={cx(
                                'tw-relative tw-w-full tw-h-full tw-overflow-hidden',
                                {'tw-flex -md:tw-flex-col tw-items-center -md:tw-justify-center': !pinParticipant}
                            )}
                            >
                                <div className={cx('handle tw-absolute tw-inset-0 tw-z-1')} />
                                <div
                                    ref={localMediaRef}
                                    id={LOCAL_MEDIA_ID}
                                    className={cx(
                                        {'pinned-media': pinParticipant === VideoPinParticipant.Local},
                                        {'unpinned-media': pinParticipant === VideoPinParticipant.Remote},
                                        {'base-media': !pinParticipant},
                                        {'tw-hidden': mini},
                                        {
                                            'full-screen-vertical': pinParticipant === VideoPinParticipant.Local &&
                                                !isLandscape && isFullScreen,
                                            'full-screen-horizontal': pinParticipant === VideoPinParticipant.Local &&
                                                isLandscape && isFullScreen,
                                        }
                                    )}
                                    onMouseMove={handleMouseMoveLocalVideo}
                                    onMouseOut={handleMouseOutLocalVideo}
                                >
                                    {localVideoOverlayVisible && overLocalVideoContent}
                                </div>
                                <div
                                    ref={remoteMediaRef}
                                    id={REMOTE_MEDIA_ID}
                                    className={cx(
                                        {'pinned-media': pinParticipant === VideoPinParticipant.Remote},
                                        {'unpinned-media': pinParticipant === VideoPinParticipant.Local},
                                        {'base-media': !pinParticipant && !mini},
                                        {
                                            'full-screen-vertical': pinParticipant === VideoPinParticipant.Remote &&
                                                !isLandscape && isFullScreen,
                                            'full-screen-horizontal': pinParticipant === VideoPinParticipant.Remote &&
                                                isLandscape && isFullScreen,
                                        })}
                                    onMouseMove={handleMouseMoveRemoteVideo}
                                    onMouseOut={handleMouseOutRemoteVideo}
                                >
                                    {/* TODO: how to detect loading state? */}
                                    {/* <Loader className='loader' svgClassName='tw-max-w-12 tw-max-h-12 screenshot-hidden' /> */}
                                    {remoteVideoOverlayVisible && overRemoteVideoContent}
                                    {remoteVideoDisabled && <div className='tw-absolute tw-inset-0 tw-bg-dark tw-z-3' />}
                                </div>
                                <div
                                    ref={otherRemoteMediaRef}
                                    id='other-remote-media'
                                    className={cx(
                                        'local-media',
                                        'tw-absolute tw-w-1/4 tw-max-w-72 tw-top-2 tw-right-2 tw-z-3 tw-rounded tw-overflow-hidden',
                                        {'tw-hidden': mini}
                                    )}
                                >
                                    {overLocalVideoContent}
                                </div>
                            </div>
                        </ResizableBoxWrapper>
                    </div>
                </Draggable>
            </div>
        );
    }
}

export default withTranslation()(KurentoVideo);
